import { Link } from "react-router-dom";
import { useMemo, useState } from "react";
import { Plus, Search } from "lucide-react";
import type { CarOptionSet } from "@/types/database";
import { resolveCarMakeLogo } from "@/lib/car-make-logos";
import { useCarMakeLogos } from "@/lib/queries";
import { formatNumber } from "@/lib/utils";
import { MakeLogo } from "@/components/cars/MakeModelGrid";
import { OptionSetWizard } from "@/components/cars/OptionSetWizard";
import { Input } from "@/components/ui/Input";
import { Workspace, WorkspaceSection, FieldLabel } from "@/components/ui/Workspace";

function OptionSetRow({ optionSet, makeLogos }: { optionSet: CarOptionSet; makeLogos: Map<string, import("@/lib/car-make-logos").CarMakeLogoEntry> | undefined }) {
  const logoEntry = optionSet.make && makeLogos ? resolveCarMakeLogo(optionSet.make, makeLogos) : null;
  const modelCount = optionSet.models?.length ?? 0;
  const trimCount = optionSet.trims?.length ?? 0;

  return (
    <Link
      to={`/option-sets/${optionSet.id}`}
      className="hover-raise flex items-center gap-4 rounded-2xl bg-canvas p-4 hover:bg-surface"
    >
      <MakeLogo make={optionSet.make} logo={logoEntry} className="h-12 w-12 object-contain" />
      <div className="min-w-0 flex-1">
        <p className="truncate font-bold">{optionSet.make}</p>
        <p className="mt-1 text-sm text-muted">
          {formatNumber(modelCount)} {modelCount === 1 ? "model" : "models"}
          {trimCount > 0 ? ` · ${formatNumber(trimCount)} ${trimCount === 1 ? "trim" : "trims"}` : null}
        </p>
      </div>
    </Link>
  );
}

export function OptionSetsExplorer({ optionSets }: { optionSets: CarOptionSet[] }) {
  const { data: makeLogos } = useCarMakeLogos();
  const [search, setSearch] = useState("");
  const [wizardOpen, setWizardOpen] = useState(false);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return optionSets;
    return optionSets.filter((optionSet) => {
      const haystack = `${optionSet.make} ${optionSet.models?.join(" ")} ${optionSet.trims?.join(" ")}`.toLowerCase();
      return haystack.includes(query);
    });
  }, [optionSets, search]);

  return (
    <>
      <Workspace>
        <WorkspaceSection className="border-b border-border/30 p-4 sm:p-5">
          <div className="flex items-end justify-between gap-4">
            <div className="flex-1">
              <FieldLabel icon={<Search className="h-3 w-3" />}>Search</FieldLabel>
              <Input
                placeholder="Make, model, trim…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <button
              type="button"
              aria-label="Create option set"
              onClick={() => setWizardOpen(true)}
              className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[var(--color-accent)] text-white opacity-100 transition hover:bg-[var(--color-accent-hover)] focus:outline-none focus:ring-2 focus:ring-accent"
            >
              <Plus className="h-5 w-5" />
            </button>
          </div>
        </WorkspaceSection>

        <WorkspaceSection border className="p-4 sm:p-5">
          <p className="mb-5 text-sm text-muted">
            <span className="font-bold text-foreground">{filtered.length}</span> option sets
          </p>

          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <p className="font-bold">No matches</p>
              <p className="mt-1 text-sm text-muted">Adjust your search or create a new option set.</p>
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((optionSet) => (
                <OptionSetRow key={optionSet.id} optionSet={optionSet} makeLogos={makeLogos} />
              ))}
            </div>
          )}
        </WorkspaceSection>
      </Workspace>

      <OptionSetWizard open={wizardOpen} onClose={() => setWizardOpen(false)} />
    </>
  );
}
